import { NoSecondTryPreview } from './NoSecondTryPreview.js';
import { isNoSecondTry } from '../lib/workItems.js';
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { DeicticSafetyFlag } from './DeicticSafetyFlag.js';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiRequestError,
  approveItem,
  approveItemWithEdit,
  cancelItem,
  commentItem,
  fetchWorkItem,
  rateItem,
} from '../api/client.js';
import { recordActivitySignal } from '../activity/signals.js';
import { useItemOpenedSignal } from '../activity/useItemOpenedSignal.js';
import { useUnsavedWorkProbe } from '../lib/unsavedWork.js';
import { BUTTON_INTERACTION_STYLES, LINK_INTERACTION_STYLES } from '../lib/actionStyles.js';
import {
  COMMENT_STATUS_STYLES,
  describeCommentDelivery,
  type CommentStatus,
} from '../lib/commentDelivery.js';
import { patchToEntity } from '../lib/draftCapture.js';
import {
  EXAM_LEVEL_OPTIONS,
  examLevelFromRatings,
  examLevelIsStale,
  examLevelRating,
  type ExamLevelChoice,
} from '../lib/examLevel.js';
import {
  buildApproveNotes,
  FOLLOW_UP_TAG_EFFECT,
  FOLLOW_UP_TAGS,
  type FollowUpTag,
} from '../lib/followUpTags.js';
import { hashPatch } from '../lib/patchHash.js';
import {
  evidenceGateApplies,
  evidenceReadiness,
  type EvidenceReadiness,
} from '../lib/evidenceReadiness.js';
import { useDraftCapture } from '../lib/useDraftCapture.js';
import {
  canConfirmRejection,
  findRejectionReason,
  getRejectionReasons,
  rejectionItemKind,
  rejectionNoteRequired,
} from '../lib/rejectionReasons.js';
import {
  buildCancelNotes,
  effectiveWorkItemPatch,
  isPracticeItem,
  queueBackPath,
  statusBadgeClass,
} from '../lib/workItems.js';
import {
  courseMismatchMessage,
  detectCourseMismatch,
  detectUndeclaredCourse,
  undeclaredCourseMessage,
} from '../lib/courseMismatch.js';
import { readCourseParam } from '../lib/courseParam.js';
import { readRenderSafetyRefusal, type RenderRefusalFinding } from '../lib/renderSafetyRefusal.js';
import type { WorkItemSummary } from '../types/workflow.js';
import { EvidenceGate } from './EvidenceGate.js';
import { PlayerPreview } from './PlayerPreview.js';
import { ReviewColumns } from './ReviewColumns.js';
import { PracticeItemMetadata } from './PracticeItemMetadata.js';

const PRACTICE_DRAFT_ENTITY_ID = 999999902;
const PRACTICE_CAPTURE_NAMESPACE = '/author-practice-draft-capture';

/**
 * Route entry point. Auto-advance (AD-30 decision 1) navigates item → item on the SAME route, which
 * would otherwise keep `PracticeItemReview` mounted and carry every piece of item-scoped state over:
 * an open rejection panel, the previous item's reason and note, an unsent comment, the preview and
 * full-screen modes. Rejecting item A and landing on item B with A's half-filled rejection form is a
 * mis-review waiting to happen, so the subtree is keyed by `workItemId` and remounts wholesale.
 * Keying beats enumerating resets: a field added later is covered automatically.
 */
export function KeyedPracticeItemReview({ viewerId }: { viewerId?: string | undefined }) {
  const { workItemId = '' } = useParams();
  return <PracticeItemReview key={workItemId} viewerId={viewerId} />;
}

export function PracticeItemReview({ viewerId }: { viewerId?: string | undefined }) {
  const { workItemId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const backPath = queueBackPath(searchParams);
  const [item, setItem] = useState<WorkItemSummary | null>(null);
  const [originalPatch, setOriginalPatch] = useState<Record<string, unknown> | null>(null);
  const [draftPatch, setDraftPatch] = useState<Record<string, unknown> | null>(null);
  const [nstrReadyId, setNstrReadyId] = useState<string | null>(null);
  const previewWorkItemId = item?.workItemId ?? null;
  const onNstrReady = useCallback((ready: boolean) => setNstrReadyId(ready ? previewWorkItemId : null), [previewWorkItemId]);
  const nstrBlocked = !!item && isNoSecondTry(item) && nstrReadyId !== workItemId;
  const [previewPatch, setPreviewPatch] = useState<Record<string, unknown> | null>(null);
  const draftPatchRef = useRef<Record<string, unknown> | null>(null);
  const renderGateRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [wrongType, setWrongType] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState<'editable' | 'vanilla'>('editable');
  const [playerFullscreen, setPlayerFullscreen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [comment, setComment] = useState('');
  const [commentStatus, setCommentStatus] = useState<CommentStatus | null>(null);
  const [evidenceAcknowledged, setEvidenceAcknowledged] = useState(false);
  const [examLevelBusy, setExamLevelBusy] = useState(false);
  const [followUpTags, setFollowUpTags] = useState<readonly FollowUpTag[]>([]);
  /**
   * The server's render-safety refusal of the last approve, and the exact bytes it was about (lane U,
   * 2026-09-26). This screen used to pass `acknowledgeRenderIssues: false` on every path, so a refusal was
   * a red banner and a dead end — Scott could approve none of twelve pilot questions. The refusal now opens
   * an explicit acknowledgement step; `patchKey` ties it to the text it measured, so an edit made after it
   * retires the question instead of carrying his confirmation onto text nobody asked him about.
   */
  const [renderRefusal, setRenderRefusal] = useState<{ findings: RenderRefusalFinding[]; patchKey: string } | null>(null);
  /** The bytes he ticked "displays correctly" for — never a bare boolean, for the same reason. */
  const [renderAcknowledgedKey, setRenderAcknowledgedKey] = useState<string | null>(null);
  // **After the item has actually loaded, not on mount.** Emitting on mount recorded a 404, a
  // wrong-type URL and a failed fetch as items opened — reading signals that credit time nobody
  // spent reading anything. `item` is only set once the fetch succeeded AND the type check passed.
  // Found by cross-model review.
  /**
   * The course this screen was OPENED for. It is what the decision declares to the server, and it
   * must never be `item.courseKey` — that would compare a value with itself and pass always.
   *
   * Read at the top rather than beside the guard below, because the activity signal needs it too.
   */
  const displayedCourseKey = readCourseParam(searchParams);
  /**
   * "This item was opened and read" is recorded ONLY when the screen actually shows the item.
   *
   * A refusal panel is not a reading. Both hard stops below — a link naming a different course, and
   * a link naming none — return before any item content renders, and this signal fired before
   * either of them: a reviewer who saw only the refusal was recorded as having opened the lesson.
   * That is not cosmetic. These rows feed the per-person activity report and the presence
   * arithmetic that grades a worked day, so a false positive here inflates a number somebody is
   * measured on. Following the declaration link flips the key from `undefined` to the id and the
   * signal is emitted then, which is the moment the item is genuinely on screen.
   */
  const itemIsActuallyShown = Boolean(item && displayedCourseKey && displayedCourseKey === item.courseKey);
  useItemOpenedSignal(itemIsActuallyShown ? workItemId : undefined);

  useEffect(() => {
    if (!workItemId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setWrongType(false);
    setError('');
    setItem(null);
    setOriginalPatch(null);
    setDraftPatch(null);
    setPreviewPatch(null);
    draftPatchRef.current = null;
    void fetchWorkItem(workItemId)
      .then((loadedItem) => {
        if (cancelled) return;
        if (!isPracticeItem(loadedItem)) {
          setWrongType(true);
          return;
        }
        const patch = effectiveWorkItemPatch(loadedItem);
        draftPatchRef.current = patch;
        setItem(loadedItem);
        setOriginalPatch(patch);
        setDraftPatch(patch);
        setPreviewPatch(patch);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        if (reason instanceof ApiRequestError && reason.status === 404) {
          setNotFound(true);
          return;
        }
        setError(reason instanceof Error ? reason.message : 'Unable to load this practice item.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  useDraftCapture({
    captureNamespace: PRACTICE_CAPTURE_NAMESPACE,
    draftEntityId: PRACTICE_DRAFT_ENTITY_ID,
    draftPatchRef,
    emptyDraftError: 'No practice item draft is loaded.',
    onPatchChange: setDraftPatch,
  });

  // A new refusal brings its panel into view: the reviewer may have scrolled the briefing past its top.
  useEffect(() => {
    if (renderRefusal) renderGateRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [renderRefusal]);

  useEffect(() => {
    if (!playerFullscreen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setPlayerFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playerFullscreen]);

  const entity = useMemo(() => {
    if (!previewPatch || !item) return null;
    return patchToEntity({
      ...previewPatch,
      groupId: previewPatch.groupId ?? item.intendedParent?.groupId,
      item_type_slug: previewPatch.item_type_slug,
    }, PRACTICE_DRAFT_ENTITY_ID);
  }, [item, previewPatch]);
  // Per-kind rejection vocabulary (§4.1). Resolved through the shared helper rather than hardcoded
  // to 'practice_item' so a course override reaches every surface the same way.
  const rejectionReasons = useMemo(
    () => (item ? getRejectionReasons(item.courseKey, rejectionItemKind(item)) : []),
    [item],
  );
  const noteRequired = rejectionNoteRequired(rejectionReasons, cancelReason);
  // Missing-evidence soft gate (AD-30 decision 2; acknowledgement narrowed to a checkbox by AD-33
  // decision 3). Approve — and only Approve — waits for the tick; reject and comment never do.
  // `evidenceGateApplies` even though this route already refuses non-practice items: one predicate
  // decides the gate's scope everywhere, or the two surfaces drift the next time it changes.
  // Conversions use the shared server verdict and their own gate/source summary.
  const readiness: EvidenceReadiness = item && !isNoSecondTry(item) && evidenceGateApplies(item) ? evidenceReadiness(item) : 'ready';
  const evidenceGateOpen = readiness === 'ready' || evidenceAcknowledged;
  // One composer for everything an approve records — the acknowledgement and any follow-up tags —
  // so no approve path invents its own note format.
  const approveNotes = buildApproveNotes({
    tags: followUpTags,
    acknowledgeEvidence: readiness !== 'ready',
  });
  const changed = Boolean(
    originalPatch && draftPatch && JSON.stringify(originalPatch) !== JSON.stringify(draftPatch),
  );
  const draftKey = draftPatch ? JSON.stringify(draftPatch) : '';
  const renderGate = renderRefusal && renderRefusal.patchKey === draftKey ? renderRefusal : null;
  const renderAcknowledged = renderGate !== null && renderAcknowledgedKey === renderGate.patchKey;
  // Declares to the stale-version reload what would be lost by reloading right now.
  //
  // Every item here is state the PAGE CANNOT SHOW as typed text, which is the whole reason the probe
  // registry exists: `changed` is the content edit, the follow-up tags and the evidence tick are
  // controls, and a chosen rejection reason is a `<select>`. Cross-model review found the first draft
  // of this list covering only the free-text fields. The two `busy` flags are here because a reload
  // mid-request abandons a mutation whose outcome we would then never learn.
  useUnsavedWorkProbe(
    changed
      || comment.trim() !== ''
      || cancelNote.trim() !== ''
      || cancelReason !== ''
      || followUpTags.length > 0
      || evidenceAcknowledged
      || renderAcknowledged
      || busy
      || examLevelBusy,
  );
  // Exam-level mark: the one control the AD asked for, a binary (owner decision 2026-07-30, which
  // supersedes §4.2's 1–5 scale). Unset by default, never required, never blocking.
  const examLevel = examLevelFromRatings(item?.latestRatings?.ratings);
  const examLevelStale = examLevelIsStale(
    item?.latestRatings,
    draftPatch ? hashPatch(draftPatch) : undefined,
  );
  const canApprove = item?.status === 'pending' || item?.status === 'cancelled';
  const canCancel = item?.status === 'pending' || item?.status === 'approved' || item?.status === 'approved_with_edit';

  if (loading) return <ReviewState message="Loading practice item…" />;
  if (notFound) return <ReviewState backPath={backPath} message="This practice item no longer exists." tone="error" />;
  if (wrongType) return <ReviewState backPath={backPath} message="This work item is not a practice item." tone="error" />;
  if (!item || !draftPatch || !entity) {
    return <ReviewState backPath={backPath} message={error || 'Unable to load this practice item.'} tone="error" />;
  }

  /**
   * HARD STOP when the link's course and the item's course disagree — never a silent redirect.
   * See `lib/courseMismatch.ts`: the approve request carries no course, and the server's course
   * check proves only that this reviewer MAY touch the item's own course. Placed above every
   * decision control, and above the item render, so no approve button is ever live under a heading
   * naming a different course.
   */
  const courseMismatch = detectCourseMismatch(displayedCourseKey, item?.courseKey);
  if (courseMismatch) {
    return <ReviewState backPath={backPath} message={courseMismatchMessage(courseMismatch)} tone="error" />;
  }
  /**
   * The SAME hard stop for a link that named no course at all. An absence is not agreement: with no
   * `?courseKey` there is nothing for `detectCourseMismatch` to compare, so this screen used to
   * render the item and its decision controls while the shell header named whichever course the
   * application had fallen back to. The exit is a link that declares the item's own course, taken by
   * the reviewer — never filled in on his behalf, because a self-comparison passes always.
   */
  const undeclaredCourse = detectUndeclaredCourse(displayedCourseKey, item?.courseKey);
  if (undeclaredCourse) {
    const openHere = `${location.pathname}?${new URLSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      courseKey: undeclaredCourse.itemCourseKey,
    }).toString()}`;
    return (
      <ReviewState
        actionHref={openHere}
        actionLabel={`Open this item in course ${undeclaredCourse.itemCourseKey}`}
        backPath={backPath}
        message={undeclaredCourseMessage(undeclaredCourse)}
        tone="error"
      />
    );
  }

  const title = typeof draftPatch.title === 'string' && draftPatch.title.trim()
    ? draftPatch.title.trim()
    : isNoSecondTry(item) ? 'One attempt review' : 'Untitled practice item';
  const itemId = item.workItemId;

  async function runAction(action: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await action();
      void navigate(backPath);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to update this practice item.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * One approve path, so the acknowledgement cannot be attached on one branch and forgotten on the
   * other. `notes` is omitted rather than passed as `undefined` when there is nothing to record:
   * an item with evidence must reach the API in exactly the call it made before this gate existed.
   */
  function submitApproval(
    workItemId: string,
    patch: Record<string, unknown> | null,
    notes: string | undefined,
    acknowledgeRenderIssues: boolean,
  ) {
    if (patch) {
      return notes
        ? approveItemWithEdit(workItemId, patch, notes, acknowledgeRenderIssues, displayedCourseKey)
        : approveItemWithEdit(workItemId, patch, undefined, acknowledgeRenderIssues, displayedCourseKey);
    }
    return notes
      ? approveItem(workItemId, notes, acknowledgeRenderIssues, displayedCourseKey)
      : approveItem(workItemId, undefined, acknowledgeRenderIssues, displayedCourseKey);
  }

  /**
   * Approve, and turn a render-safety refusal into the acknowledgement step rather than an error.
   *
   * The acknowledgement is sent ONLY when the reviewer ticked it for exactly these bytes, and a refusal
   * of an acknowledged request is shown as an error — never re-asked, so this cannot loop. Everything
   * else the decision carries (the edited patch, the evidence acknowledgement and follow-up tags in
   * `approveNotes`, the course) is the same on the retry as on the first attempt.
   */
  async function approve(workItemId: string, carriesReviewerPatch: boolean) {
    if (nstrBlocked) return;
    const acknowledge = !item || !isNoSecondTry(item) ? renderAcknowledged : false;
    const patchKey = draftKey;
    setBusy(true);
    setError('');
    try {
      // THE BYTES ON SCREEN ARE THE BYTES DECIDED. An item that already carries a reviewer's edit (a
      // cancelled approve-with-edit being approved again) is shown as that edit; a plain approve would make
      // the server measure and ship the imported patch and discard the edit, so the reviewer — and his
      // render acknowledgement — would vouch for text he never saw. Same rule as `MicroLessonReview`.
      // Found by cross-model review (gpt-6-astra) of lane U's diff; the dispatch predates lane U.
      await submitApproval(workItemId, item && isNoSecondTry(item) ? null : changed || carriesReviewerPatch ? draftPatch : null, approveNotes, acknowledge);
      void navigate(backPath);
    } catch (reason) {
      const findings = acknowledge || item && isNoSecondTry(item) ? null : readRenderSafetyRefusal(reason, draftPatch);
      if (findings) {
        setRenderRefusal({ findings, patchKey });
        setRenderAcknowledgedKey(null);
      } else {
        setError(reason instanceof Error ? reason.message : 'Unable to update this practice item.');
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Saves on the click — the mark is one tap, so a second "save" step would spend the reviewer
   * seconds this control exists to give back. A failure surfaces in the error banner and leaves the
   * previous mark in place; it never touches the approve path, because the mark is never blocking.
   */
  async function markExamLevel(choice: ExamLevelChoice) {
    if (examLevelBusy || choice === examLevel) return;
    setExamLevelBusy(true);
    setError('');
    try {
      const updated = await rateItem(itemId, examLevelRating(choice));
      setItem(updated);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save the exam-level mark.');
    } finally {
      setExamLevelBusy(false);
    }
  }

  async function postComment() {
    const text = comment.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    setCommentStatus(null);
    try {
      const result = await commentItem(itemId, text, displayedCourseKey);
      setComment('');
      setCommentStatus(describeCommentDelivery(result.postedToSlack));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to post the comment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background-light">
      <header className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link className={`text-xs font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} to={backPath}>← Back to queue</Link>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-black tracking-tight text-slate-900">{title}</h1>
              <span className={statusBadgeClass(item.status)}>{item.status.replaceAll('_', ' ')}</span>
            </div>
          </div>
          {changed ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
              <span className="size-2 rounded-full bg-amber-500" />
              <span>Unsaved player edits</span>
              <button
                className={`rounded border border-amber-300 bg-white px-2 py-1 hover:bg-amber-100 ${BUTTON_INTERACTION_STYLES}`}
                onClick={() => {
                  if (!originalPatch) return;
                  draftPatchRef.current = originalPatch;
                  setDraftPatch(originalPatch);
                  setPreviewPatch(originalPatch);
                }}
                type="button"
              >
                Discard edits
              </button>
            </div>
          ) : null}
        </div>
        {error ? <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs font-semibold text-rose-700">{error}</p> : null}
      </header>

      {/* Player first, with independently scrolling review controls beside it. */}
      <ReviewColumns viewerId={viewerId} page="practice" defaultLeftWidth={864} minRightWidth={416} fullscreen={playerFullscreen}>
        {isNoSecondTry(item) ? <NoSecondTryPreview item={item} onApprovalReady={onNstrReady} /> : (

        <section
          aria-label="Practice item player"
          className={playerFullscreen
            ? 'fixed inset-0 z-50 flex min-h-0 flex-col overflow-hidden bg-white shadow-sm'
            : 'flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'}
        >
          <div className={`flex items-center justify-between gap-3 border-b border-slate-200 py-3 ${playerFullscreen ? 'px-6' : 'px-4'}`}>
            <div>
              <h2 className="text-sm font-bold text-slate-900">{playerFullscreen ? title : 'Player preview'}</h2>
              <p className="text-xs text-slate-500">
                {previewMode === 'editable'
                  ? 'Click text inside the player to edit it in place.'
                  : 'Learner preview — editing is off.'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div aria-label="Player mode" className="flex overflow-hidden rounded-lg border border-slate-300" role="group">
                <button
                  aria-pressed={previewMode === 'editable'}
                  className={`${previewMode === 'editable' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'} px-3 py-1.5 text-xs font-bold ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setPreviewMode('editable')}
                  type="button"
                >
                  Review &amp; edit
                </button>
                <button
                  aria-pressed={previewMode === 'vanilla'}
                  className={`${previewMode === 'vanilla' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'} border-l border-slate-300 px-3 py-1.5 text-xs font-bold ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setPreviewMode('vanilla')}
                  type="button"
                >
                  Learner preview
                </button>
              </div>
              <button
                aria-label={playerFullscreen ? 'Exit full screen' : 'Enter full screen'}
                className={`grid size-8 place-items-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 ${BUTTON_INTERACTION_STYLES}`}
                onClick={() => {
                  setPlayerFullscreen((fullscreen) => !fullscreen);
                  recordActivitySignal({ kind: 'player_fullscreen_toggled', stepId: null });
                }}
                title={playerFullscreen ? 'Exit full screen' : 'Enter full screen'}
                type="button"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                  {playerFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                </span>
              </button>
            </div>
          </div>
          <div className="relative min-h-0 flex-1">
            {/* PlayerPreview keeps the graph below the item at every mount width. */}
            <div className={`absolute inset-0 w-full ${previewMode === 'editable' ? 'overflow-y-auto' : 'overflow-hidden'}`} data-testid="practice-player-column">
              <PlayerPreview
                expandWorkflow
                entity={entity}
                mode={previewMode}
                reserveAiPaneWidth={false}
                {...(previewMode === 'editable' ? { saveNamespace: PRACTICE_CAPTURE_NAMESPACE } : {})}
              />
            </div>
          </div>
        </section>
        )}

        {/* Everything that is not the item. Scrolls on its own so a long metadata block never
            steals height from the player, and the decision controls stay where the reviewer left
            them. Ordered as the review runs: what am I looking at, then the bar, then the call. */}
        <div className="flex min-h-0 flex-col" data-testid="practice-controls-column">
        {/* Only the briefing scrolls. */}
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {/* In the briefing, not the pinned footer: the panel is ~270px, and in the footer it pushed the
              Approve button below the fold at 1366x768 in the browser pass — on a screen whose outer
              column does not scroll. It is scrolled into view when it appears. */}
          <DeicticSafetyFlag item={item} changed={changed} />
          {canApprove && renderGate && !isNoSecondTry(item) ? (
            <div ref={renderGateRef}>
              <RenderSafetyGate
                acknowledged={renderAcknowledged}
                findings={renderGate.findings}
                onAcknowledge={(acknowledged) => setRenderAcknowledgedKey(acknowledged ? renderGate.patchKey : null)}
              />
            </div>
          ) : null}
          {readiness !== 'ready' ? (
            <EvidenceGate
              acknowledged={evidenceAcknowledged}
              onAcknowledge={setEvidenceAcknowledged}
              readiness={readiness}
            />
          ) : null}
          <section className="mb-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <PracticeItemMetadata item={{ ...item, suggestedPatch: draftPatch }} />
          </section>
        </div>
        {/* Keep decisions pinned when they fit. Narrow columns, an open rejection panel or a tall
            comment can outgrow the pane; scroll this area rather than clipping the final actions. */}
        <div aria-label="Practice review decisions" className="min-h-0 shrink-0 overflow-y-auto border-t border-slate-200 bg-white pt-3" style={{ maxHeight: '75%' }}>
        {cancelOpen ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
            <span className="text-xs font-bold uppercase text-slate-500">Why reject this draft?</span>
            {rejectionReasons.map((reason) => (
              <button
                aria-pressed={cancelReason === reason.label}
                className={`${cancelReason === reason.label ? 'bg-rose-600 text-white' : 'border border-slate-200 bg-white text-slate-600'} rounded-full px-2.5 py-1 text-[11px] font-bold ${BUTTON_INTERACTION_STYLES}`}
                key={reason.label}
                onClick={() => setCancelReason((current) => current === reason.label ? '' : reason.label)}
                title={reason.hint}
                type="button"
              >
                {reason.label}
              </button>
            ))}
            {/* `basis-full`: a multi-line box on the same wrapping row as the six reason chips and
                two buttons would set that row's height to the box's, pushing the buttons out of
                reach. Its own line also gives the reviewer the full panel width — his rejection
                notes run to several sentences (AD intake 2026-08-06, `IN-20260806-2`). */}
            <textarea
              aria-label="Rejection note"
              aria-required={noteRequired}
              className="basis-full rounded border border-slate-300 p-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              maxLength={4000}
              onChange={(event) => setCancelNote(event.target.value)}
              placeholder={noteRequired ? 'Required — say what is wrong…' : 'Optional note…'}
              rows={3}
              value={cancelNote}
            />
            <button
              className={`rounded bg-rose-600 px-3 py-1.5 text-xs font-bold text-white ${BUTTON_INTERACTION_STYLES}`}
              disabled={busy || !canConfirmRejection(rejectionReasons, cancelReason, cancelNote)}
              onClick={() => void runAction(() => cancelItem(item.workItemId, buildCancelNotes(cancelReason, cancelNote), displayedCourseKey))}
              type="button"
            >
              Confirm rejection
            </button>
            <button className={`rounded px-2 py-1.5 text-xs font-bold text-slate-500 ${BUTTON_INTERACTION_STYLES}`} onClick={() => setCancelOpen(false)} type="button">Back</button>
            {/* §4.3: the line whose absence cost both session-05 items. Both cancellations were level
                disputes on items that were otherwise fine, and the honest levels were inside the range
                the reviewer says he wants — so the alternative belongs where the rejection is decided,
                not in a document. Phrased against the binary control, not the removed band. */}
            <p className="basis-full text-[11px] font-semibold text-slate-700">
              Not exam level? Mark it &quot;Sub-exam level&quot; and approve — no need to reject.
            </p>
            {/* The chip's `title` alone reaches neither keyboard nor screen-reader users, and the
                hints are what keep two reviewers picking the same reason for the same defect.
                `aria-live` rather than `role="status"`: this surface already has one status region
                (the Slack delivery notice) and two would make `getByRole('status')` ambiguous. */}
            <p aria-live="polite" className="basis-full text-[11px] leading-relaxed text-slate-600" data-testid="rejection-reason-hint">
              {findRejectionReason(rejectionReasons, cancelReason)?.hint ?? ''}
            </p>
          </div>
        ) : null}
        {/* Both are approve-time apparatus, so both are gated on the approve being possible. The bar
            is decision guidance at the moment of decision, and the tag row has no transport of its
            own — the tags ride the approve's `notes`, so on an item the API will refuse to approve
            (`applyReviewAction` 409s on any status but pending/cancelled) every tap would be
            discarded silently while the row said "Recorded on the item". */}
        {canApprove ? (
          <>
            <ApprovalBar />
            <FollowUpTagRow
              onToggle={(tag) => setFollowUpTags((current) => current.includes(tag)
                ? current.filter((candidate) => candidate !== tag)
                : [...current, tag])}
              selected={followUpTags}
            />
          </>
        ) : null}
        {/* The one control the AD asked for. Deliberately NOT a scale and deliberately not labelled
            "difficulty": he has just been told difficulty assessment is off his plate (AD-34), so a
            numeric-looking control here would read as the thing that was taken away. The parenthetical
            says what the mark DOES, which is the whole reason the wording is what it is. */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase text-slate-500">Exam level</span>
          <div aria-label="Exam level" className="flex overflow-hidden rounded-lg border border-slate-300" role="group">
            {EXAM_LEVEL_OPTIONS.map((option, index) => (
              <button
                aria-pressed={examLevel === option.choice}
                className={`${examLevel === option.choice
                  ? 'bg-indigo-700 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'} ${index > 0 ? 'border-l border-slate-300' : ''} px-3 py-1.5 text-xs font-bold ${BUTTON_INTERACTION_STYLES}`}
                disabled={examLevelBusy}
                key={option.choice}
                onClick={() => void markExamLevel(option.choice)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
          {/* The unset copy discloses both properties a reviewer would otherwise have to discover:
              it never blocks, and there is no un-mark (the ratings API has no delete, and a third
              "clear" choice would breach the two-option decision). */}
          <span aria-live="polite" className="text-[11px] font-semibold text-slate-500">
            {examLevelBusy
              ? 'Saving…'
              : examLevel
                ? examLevelStale
                  ? 'Marked on an earlier version of this item'
                  : 'Mark saved'
                : 'Optional — never blocks approval. Saves on click and can be switched, not cleared.'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canApprove ? (
            <button
              className={`rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 ${BUTTON_INTERACTION_STYLES}`}
              disabled={busy || (isNoSecondTry(item) ? nstrBlocked : !evidenceGateOpen || (renderGate !== null && !renderAcknowledged))}
              onClick={() => void approve(item.workItemId, Boolean(item.reviewerPatch))}
              title={isNoSecondTry(item) ? undefined : !evidenceGateOpen
                ? 'Tick the missing-evidence acknowledgement above to approve.'
                : renderGate !== null && !renderAcknowledged
                  ? 'Check the learner preview, then tick the render-check confirmation above to approve.'
                  : undefined}
              type="button"
            >
              {isNoSecondTry(item) ? 'Approve' : `${changed ? 'Approve with edits' : 'Approve'}${renderGate ? ' anyway' : ''}`}
            </button>
          ) : null}
          {canCancel ? (
            <button className={`rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`} onClick={() => setCancelOpen(true)} type="button">Reject draft…</button>
          ) : null}
          {/* `items-start` so the Post button stays at the top of a box the reviewer has dragged
              taller, rather than drifting down the footer with it. The old `max-w-lg` cap is gone:
              on a wide display it pinned the box to ~32rem with the rest of the footer empty,
              which is what the screenshot in `IN-20260806-2` was showing. */}
          <div className="ml-auto flex min-w-72 flex-1 items-start justify-end gap-2">
            <textarea
              aria-label="Comment"
              className="min-w-0 flex-1 rounded-lg border border-slate-300 p-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              maxLength={4000}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Add a comment — posts to Slack…"
              rows={3}
              value={comment}
            />
            <button className={`shrink-0 rounded-lg bg-slate-800 px-3 py-2 text-xs font-bold text-white ${BUTTON_INTERACTION_STYLES}`} disabled={busy || !comment.trim()} onClick={() => void postComment()} type="button">Post comment</button>
          </div>
        </div>
        {/* Always mounted: a live region created together with its text is not reliably
            announced, so the reviewer would never hear that Slack delivery failed. */}
        <p
          className={commentStatus
            ? `mt-2 text-right text-xs font-semibold ${COMMENT_STATUS_STYLES[commentStatus.tone]}`
            : 'sr-only'}
          role="status"
        >
          {commentStatus?.message ?? ''}
        </p>
        </div>
        </div>
      </ReviewColumns>
    </main>
  );
}

/**
 * The render-check acknowledgement (lane U, 2026-09-26).
 *
 * Shown only after the server refused an approve over a blocking render finding on exactly the text in
 * front of the reviewer. It names each finding as a place and a plain-words problem with the text itself,
 * sends him to the learner preview — the same player a learner gets — and asks for one explicit tick.
 * The copy says, before the tick, what it does NOT do — release stays blocked while the finding remains —
 * because an acknowledgement that sounded like a waiver would be a promise the release check does not keep. `role="alert"` is deliberate here, unlike the evidence gate:
 * it appears in response to his own click, once, and it is the reason nothing happened.
 */
function RenderSafetyGate({
  acknowledged,
  findings,
  onAcknowledge,
}: {
  acknowledged: boolean;
  findings: RenderRefusalFinding[];
  onAcknowledge: (acknowledged: boolean) => void;
}) {
  return (
    <section
      className="mb-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-amber-950"
      data-testid="render-safety-gate"
      role="alert"
    >
      <h3 className="text-xs font-black uppercase tracking-wide text-amber-900">The render check flagged this question</h3>
      {/* Guidance and the tick FIRST: on a 768px-high screen only the top of this panel is in view, and
          the one thing he must be able to reach is the confirmation. */}
      <p className="mt-1 text-xs leading-relaxed">
        Look at it in <span className="font-bold">Learner preview</span>. If it displays correctly, tick below and
        approve; if it does not, reject the draft instead.
      </p>
      {/* What the tick buys, BEFORE the tick (cross-model review): the release script refuses any stored
          render finding and has no override (`L-4594`), so an earlier "we will look at it" promised a
          follow-up nothing performs. */}
      <p className="mt-1 text-xs font-bold leading-relaxed">
        Approving records your confirmation; release stays blocked while this finding remains.
      </p>
      <label className="mt-2 flex items-center gap-2 text-xs font-bold text-amber-900">
        <input
          checked={acknowledged}
          className="size-4 rounded border-amber-400 text-amber-600 focus-visible:ring-2 focus-visible:ring-amber-500"
          onChange={(event) => onAcknowledge(event.target.checked)}
          type="checkbox"
        />
        I checked the learner preview and this question displays correctly.
      </label>
      <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-amber-800">What the check found</p>
      {findings.length > 0 ? (
        <ul className="mt-1 space-y-1 pl-4 text-xs">
          {findings.map((finding, index) => (
            <li className="list-disc" key={`${finding.place}-${finding.reason}-${index}`}>
              <span className="font-bold">{finding.place}</span>
              {` ${finding.problem}`}
              {finding.sample ? (
                <>
                  {' — '}
                  <code className="break-all rounded bg-white/70 px-1 font-mono text-[11px]">{finding.sample}</code>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs italic">The check did not say where.</p>
      )}
    </section>
  );
}

/**
 * The approval bar (§4.3, owner-directed policy).
 *
 * "Approve anything merely good enough to put in front of a candidate today; polish is a later pass."
 * That policy existed only in a document, while the reviewer made the decision on this screen — so it
 * renders here, in the footer, where the decision is actually taken. Collapsed by default: the one
 * line is the message and the lists are the reference, and a permanently expanded block would spend
 * the vertical space AD-30 decision 5 asks to reclaim.
 *
 * The AD already reasons this way unprompted (session 03, verbatim: *"So I'll approve. There's nothing
 * wrong with this. Again, engagement could be better… but that's not a blocker in terms of quality, so
 * it approves."*). This reinforces demonstrated behaviour rather than installing new behaviour.
 */
function ApprovalBar() {
  return (
    <div
      className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
      data-testid="approval-bar"
    >
      {/* "tag it" names the follow-up row below, which ships in the same commit as this clause — it
          was deliberately withheld while there was nothing to tag with (gpt-5.6-sol, 2026-07-30). */}
      <p className="text-xs font-bold text-emerald-900">
        Approve anything good enough to put in front of a candidate today. Polish is a later pass — tag
        it and let it ship.
      </p>
      <details className="mt-1 text-[11px] leading-relaxed text-emerald-950">
        <summary className={`cursor-pointer font-bold ${BUTTON_INTERACTION_STYLES}`}>
          What clears the bar, what does not block, what does
        </summary>
        <div className="mt-1.5 grid gap-2 md:grid-cols-3">
          <div>
            <h4 className="font-bold uppercase tracking-wide text-emerald-800">Clears the bar</h4>
            <p>
              The key is right, the stem is true, the options are honest, and it renders. &quot;Not the
              item I would have written&quot; is not a reason to reject.
            </p>
          </div>
          <div>
            <h4 className="font-bold uppercase tracking-wide text-emerald-800">Does not block</h4>
            <p>
              A thin explanation, one weak distractor, punctuation, a formula written in prose rather
              than display style, or an item that is not exam level — mark it Sub-exam level instead.
            </p>
          </div>
          <div>
            <h4 className="font-bold uppercase tracking-wide text-rose-800">Does block</h4>
            <p>
              A wrong answer key, something factually wrong, content outside the current curriculum, or
              an item that will not render.
            </p>
          </div>
        </div>
      </details>
    </div>
  );
}

/**
 * Follow-up tags on approve (§4.3, owner-approved 2026-07-30).
 *
 * "Approved, but this needs work later", for the defect classes the programme has already measured.
 * Nothing is submitted when a chip is tapped and nothing is blocked by one: the selection rides the
 * approve's own `notes`, so tagging costs the reviewer no request and no wait.
 *
 * The effect line is the honest one. §4.3 proposed "Tagged. This opens an improvement item and does
 * not affect release", and no improvement item exists — a reviewer who is told work was created and
 * sees nothing happen stops tapping, which is how eight rating signals came to sit in the database
 * producing nothing.
 */
function FollowUpTagRow({
  onToggle,
  selected,
}: {
  onToggle: (tag: FollowUpTag) => void;
  selected: readonly FollowUpTag[];
}) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="follow-up-tags">
      <span className="text-xs font-bold uppercase text-slate-500">Needs work later</span>
      <div aria-label="Needs work later" className="flex flex-wrap gap-1.5" role="group">
        {FOLLOW_UP_TAGS.map((tag) => (
          <button
            aria-pressed={selected.includes(tag)}
            className={`${selected.includes(tag)
              ? 'bg-amber-500 text-white'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'} rounded-full px-2.5 py-1 text-[11px] font-bold ${BUTTON_INTERACTION_STYLES}`}
            key={tag}
            onClick={() => onToggle(tag)}
            type="button"
          >
            {tag}
          </button>
        ))}
      </div>
      <span aria-live="polite" className="text-[11px] font-semibold text-slate-500">
        {selected.length > 0 ? FOLLOW_UP_TAG_EFFECT : 'Optional — tagging never holds the item back.'}
      </span>
    </div>
  );
}

function ReviewState({ message, tone = 'neutral', backPath, actionHref, actionLabel }: {
  message: string;
  tone?: 'neutral' | 'error';
  backPath?: string;
  /** A way FORWARD out of a refusal. Used by the undeclared-course stop, whose whole point is that
   *  the reviewer — not the code — states which course he is looking at. */
  actionHref?: string;
  actionLabel?: string;
}) {
  return (
    <main className="grid flex-1 place-items-center bg-background-light p-6">
      <div className={tone === 'error' ? 'rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800' : 'text-sm text-slate-500'}>
        <p>{message}</p>
        <div className="flex items-center gap-4">
          {actionHref && actionLabel ? (
            <Link
              className={`mt-3 inline-block text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`}
              data-testid="declare-course-action"
              to={actionHref}
            >
              {actionLabel}
            </Link>
          ) : null}
          {backPath ? <Link className={`mt-3 inline-block text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} to={backPath}>← Back to queue</Link> : null}
        </div>
      </div>
    </main>
  );
}
